import { isValidElement, useEffect, useMemo, useState } from "react";
import { IChildren } from "lib/common/interfaces";
import { IChildrenCacheProps } from "./interfaces";
import { renderToString } from "react-dom/server";

function childrenKey(children: IChildren["children"]) {
  if (isValidElement(children)) {
    return renderToString(children);
  }

  return `${children}`;
}

/**
 * Caches all variants of children, keyed by their string value.
 */
export function useChildrenCache({ cacheEnabled, children }: IChildrenCacheProps) {
  const cacheKey = useMemo(() => childrenKey(children), [children]);
  const [childrenCache, setChildrenCache] = useState<Record<string, IChildren["children"]>>({
    [cacheKey]: children,
  });

  const childrenCacheEntries = useMemo(() => Object.entries(childrenCache), [childrenCache]);

  useEffect(() => {
    if (cacheEnabled) {
      setChildrenCache(prev => ({
        ...prev,
        [cacheKey]: children,
      }));
    }
  }, [cacheEnabled, cacheKey, children]);

  return {
    childrenCache,
    childrenCacheEntries,
    cacheKey
  };
}
